/**
 * LegendarySystem.js
 *
 * Phase 4 (June 2026) — visual + reward layer for legendary "wonder" worlds.
 *
 * When a body is legendary, this builds a striking archetype landmark near the
 * surface spawn (the in-world "something is special here" hook) plus a cluster
 * of extra-rich, over-sized mineable rocks (the guaranteed ore jackpot). The
 * rich rocks are handed back to GameScene which registers them with the
 * MiningSystem, so yield scales up exactly like any other rock — just bigger.
 *
 * Archetypes: crystalline | bioluminescent | ringed | volcanic.
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
} from '@babylonjs/core';

// Small deterministic RNG so the wonder looks identical every visit.
function makeRng(seed) {
  let s = (seed >>> 0) || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export default class LegendarySystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.glow = opts.glow || null;
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);
    this.root = new TransformNode('legendaryRoot', scene);
    this._lights = [];
    this._t = 0;
    this._pulse = [];
  }

  /** Build the wonder. Returns { richRocks } for mining registration. */
  build(archetype, seed = 1, center = { x: 0, z: 0 }) {
    const rng = makeRng(seed);
    const cx = center.x;
    const cz = center.z;
    let richRocks = [];

    if (archetype === 'crystalline') richRocks = this._buildCrystalline(cx, cz, rng);
    else if (archetype === 'bioluminescent') richRocks = this._buildBioluminescent(cx, cz, rng);
    else if (archetype === 'ringed') richRocks = this._buildRinged(cx, cz, rng);
    else if (archetype === 'volcanic') richRocks = this._buildVolcanic(cx, cz, rng);
    else richRocks = this._buildCrystalline(cx, cz, rng);

    return { richRocks };
  }

  _emissiveMat(name, r, g, b) {
    const m = new StandardMaterial(name, this.scene);
    m.emissiveColor = new Color3(r, g, b);
    m.diffuseColor = new Color3(r * 0.3, g * 0.3, b * 0.3);
    m.specularColor = new Color3(0.1, 0.1, 0.1);
    m.maxSimultaneousLights = 8;
    return m;
  }

  _addLight(x, y, z, col, range = 30, intensity = 1.0, pulse = true) {
    const l = new PointLight(`legLight_${this._lights.length}`, new Vector3(x, y, z), this.scene);
    l.diffuse = col;
    l.intensity = intensity;
    l.range = range;
    l.parent = this.root;
    this._lights.push({ light: l, base: intensity, phase: this._lights.length * 0.7, pulse });
    return l;
  }

  // ---- rich ore rocks shared by every archetype (the jackpot cluster) ----
  _richRocks(cx, cz, rng, tint) {
    const rocks = [];
    const mat = new StandardMaterial(`legRichMat_${cx}_${cz}`, this.scene);
    mat.diffuseColor = tint.scale(0.5);
    mat.emissiveColor = tint.scale(0.35);
    mat.specularColor = new Color3(0.2, 0.2, 0.2);
    mat.maxSimultaneousLights = 8;
    const n = 6 + Math.floor(rng() * 4); // 6..9 rich rocks
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const d = 8 + rng() * 18;
      const x = cx + Math.cos(a) * d;
      const z = cz + Math.sin(a) * d;
      const size = 9 + rng() * 7; // big → high yield
      const r = MeshBuilder.CreatePolyhedron(`legRock_${i}_${cx}_${cz}`, {
        type: Math.floor(rng() * 4), size,
      }, this.scene);
      const h = this.getTerrainHeight(x, z);
      r.position = new Vector3(x, h + size * 0.3, z);
      r.rotation = new Vector3(rng() * Math.PI, rng() * Math.PI, rng() * Math.PI);
      r.material = mat;
      r.isPickable = false;
      r.parent = this.root;
      if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(r); } catch (_e) { /* noop */ } }
      rocks.push({ id: `leg_${Math.round(x)}_${Math.round(z)}`, pos: r.position.clone(), radius: size, mesh: r });
    }
    return rocks;
  }

  _buildCrystalline(cx, cz, rng) {
    const col = new Color3(0.3, 0.85, 1.0);
    const mat = this._emissiveMat(`legCrystalMat_${cx}_${cz}`, 0.25, 0.8, 1.0);
    const n = 9 + Math.floor(rng() * 5);
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const d = rng() * 22;
      const x = cx + Math.cos(a) * d;
      const z = cz + Math.sin(a) * d;
      const h = 14 + rng() * 26;
      const spire = MeshBuilder.CreateCylinder(`legCrystal_${i}`, {
        diameterTop: 0, diameterBottom: 2 + rng() * 3.5, height: h, tessellation: 6,
      }, this.scene);
      const gy = this.getTerrainHeight(x, z);
      spire.position = new Vector3(x, gy + h * 0.5, z);
      spire.rotation.z = (rng() - 0.5) * 0.4;
      spire.material = mat;
      spire.isPickable = false;
      spire.parent = this.root;
      if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(spire); } catch (_e) { /* noop */ } }
    }
    this._addLight(cx, this.getTerrainHeight(cx, cz) + 20, cz, col, 70, 1.4);
    return this._richRocks(cx, cz, rng, col);
  }

  _buildBioluminescent(cx, cz, rng) {
    const col = new Color3(0.3, 1.0, 0.55);
    const capMat = this._emissiveMat(`legBioCap_${cx}_${cz}`, 0.25, 1.0, 0.5);
    const stemMat = new StandardMaterial(`legBioStem_${cx}_${cz}`, this.scene);
    stemMat.diffuseColor = new Color3(0.2, 0.35, 0.25);
    stemMat.maxSimultaneousLights = 8;
    const n = 12 + Math.floor(rng() * 8);
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const d = rng() * 26;
      const x = cx + Math.cos(a) * d;
      const z = cz + Math.sin(a) * d;
      const gy = this.getTerrainHeight(x, z);
      const sh = 4 + rng() * 9;
      const stem = MeshBuilder.CreateCylinder(`legBioStem_${i}`, { diameterTop: 0.6, diameterBottom: 1.0, height: sh, tessellation: 8 }, this.scene);
      stem.position = new Vector3(x, gy + sh * 0.5, z);
      stem.material = stemMat;
      stem.isPickable = false;
      stem.parent = this.root;
      const cap = MeshBuilder.CreateSphere(`legBioCap_${i}`, { diameter: 2.5 + rng() * 2.5, segments: 10 }, this.scene);
      cap.scaling.y = 0.55;
      cap.position = new Vector3(x, gy + sh + 0.4, z);
      cap.material = capMat;
      cap.isPickable = false;
      cap.parent = this.root;
      if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(cap); } catch (_e) { /* noop */ } }
    }
    // Glowing ground pools
    const poolMat = this._emissiveMat(`legBioPool_${cx}_${cz}`, 0.15, 0.9, 0.6);
    poolMat.disableLighting = true;
    for (let i = 0; i < 5; i++) {
      const a = rng() * Math.PI * 2;
      const d = 6 + rng() * 24;
      const x = cx + Math.cos(a) * d;
      const z = cz + Math.sin(a) * d;
      const disc = MeshBuilder.CreateDisc(`legBioDisc_${i}`, { radius: 3 + rng() * 5, tessellation: 20 }, this.scene);
      disc.rotation.x = Math.PI / 2;
      disc.position = new Vector3(x, this.getTerrainHeight(x, z) + 0.15, z);
      disc.material = poolMat;
      disc.isPickable = false;
      disc.parent = this.root;
    }
    this._addLight(cx, this.getTerrainHeight(cx, cz) + 10, cz, col, 60, 1.1);
    return this._richRocks(cx, cz, rng, col);
  }

  _buildRinged(cx, cz, rng) {
    const col = new Color3(0.78, 0.72, 1.0);
    // A huge tilted ring arcing across the sky over the spawn.
    const ringMat = this._emissiveMat(`legRing_${cx}_${cz}`, 0.6, 0.55, 0.85);
    ringMat.alpha = 0.85;
    const gy = this.getTerrainHeight(cx, cz);
    for (let k = 0; k < 3; k++) {
      const ring = MeshBuilder.CreateTorus(`legRing_${k}`, {
        diameter: 220 + k * 60, thickness: 6 + k * 3, tessellation: 64,
      }, this.scene);
      ring.position = new Vector3(cx, gy + 120, cz);
      ring.rotation.x = 1.05;
      ring.rotation.z = 0.25;
      ring.material = ringMat;
      ring.isPickable = false;
      ring.parent = this.root;
      if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(ring); } catch (_e) { /* noop */ } }
    }
    // A few banded monoliths at the base.
    const monoMat = this._emissiveMat(`legMono_${cx}_${cz}`, 0.35, 0.32, 0.5);
    for (let i = 0; i < 5; i++) {
      const a = rng() * Math.PI * 2;
      const d = 8 + rng() * 18;
      const x = cx + Math.cos(a) * d;
      const z = cz + Math.sin(a) * d;
      const h = 10 + rng() * 16;
      const mono = MeshBuilder.CreateBox(`legMono_${i}`, { width: 3, height: h, depth: 3 }, this.scene);
      mono.position = new Vector3(x, this.getTerrainHeight(x, z) + h * 0.5, z);
      mono.rotation.y = rng() * Math.PI;
      mono.material = monoMat;
      mono.isPickable = false;
      mono.parent = this.root;
    }
    this._addLight(cx, gy + 30, cz, col, 80, 1.0);
    return this._richRocks(cx, cz, rng, col);
  }

  _buildVolcanic(cx, cz, rng) {
    const col = new Color3(1.0, 0.45, 0.12);
    // Central obsidian spire.
    const obsMat = new StandardMaterial(`legObs_${cx}_${cz}`, this.scene);
    obsMat.diffuseColor = new Color3(0.05, 0.04, 0.05);
    obsMat.specularColor = new Color3(0.3, 0.15, 0.1);
    obsMat.maxSimultaneousLights = 8;
    const gy = this.getTerrainHeight(cx, cz);
    const spire = MeshBuilder.CreateCylinder(`legObsSpire_${cx}_${cz}`, { diameterTop: 1, diameterBottom: 10, height: 38, tessellation: 7 }, this.scene);
    spire.position = new Vector3(cx, gy + 19, cz);
    spire.material = obsMat;
    spire.isPickable = false;
    spire.parent = this.root;
    // Glowing lava cracks/pools radiating out.
    const lavaMat = this._emissiveMat(`legLava_${cx}_${cz}`, 1.0, 0.4, 0.08);
    lavaMat.disableLighting = true;
    const n = 8 + Math.floor(rng() * 6);
    for (let i = 0; i < n; i++) {
      const a = rng() * Math.PI * 2;
      const d = 5 + rng() * 26;
      const x = cx + Math.cos(a) * d;
      const z = cz + Math.sin(a) * d;
      const disc = MeshBuilder.CreateDisc(`legLavaDisc_${i}`, { radius: 2.5 + rng() * 6, tessellation: 18 }, this.scene);
      disc.rotation.x = Math.PI / 2;
      disc.position = new Vector3(x, this.getTerrainHeight(x, z) + 0.18, z);
      disc.material = lavaMat;
      disc.isPickable = false;
      disc.parent = this.root;
      this._pulse.push(disc);
    }
    this._addLight(cx, gy + 16, cz, col, 75, 1.6);
    return this._richRocks(cx, cz, rng, col);
  }

  tick(dt) {
    this._t += dt;
    for (const l of this._lights) {
      if (!l.light || !l.pulse) continue;
      l.light.intensity = l.base * (0.7 + 0.3 * Math.sin(this._t * 1.8 + l.phase));
    }
    for (const d of this._pulse) {
      if (d?.material) {
        const k = 0.7 + 0.3 * Math.sin(this._t * 3 + (d.uniqueId % 7));
        try { d.material.emissiveColor = new Color3(1.0 * k, 0.4 * k, 0.08 * k); } catch (_e) { /* noop */ }
      }
    }
  }

  dispose() {
    try { this.root.dispose(false, true); } catch (_e) { /* noop */ }
    this._lights = [];
    this._pulse = [];
  }
}
